import { Box, IconButton, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import FileAttachment from '../../atoms/dashboard-details-v2/FileAttachment';
import { CompanyFile } from '../../../types/files';
import { scrollbarStyle } from '../../../shared/dashboard';

const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3 5l4 4 4-4" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3 9l4-4 4 4" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SCROLL_THRESHOLD = 5;
const VISIBLE_ITEMS = 4;
const ITEM_HEIGHT = 44;
const ITEM_GAP = 8;

const useStyles = makeStyles((theme) => ({
    root: { },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    label: {
        fontSize: 12,
        color: '#6c757d',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    toggleBtn: {
        padding: 2,
        borderRadius: 4,
        '&:hover': {
            backgroundColor: '#e9ecef',
        },
    },
    listScrollable: {
        maxHeight: (ITEM_HEIGHT * VISIBLE_ITEMS) + (ITEM_GAP * (VISIBLE_ITEMS - 1)),
        overflowY: 'auto',
        paddingRight: theme.spacing(0.5),
        ...scrollbarStyle,
    },
}));

type EmailDocumentsSectionProps = {
    files: CompanyFile[];
    links: string[];
    allFiles: CompanyFile[];
    onFileClick: (file: CompanyFile) => void;
};

const EmailDocumentsSection: React.FC<EmailDocumentsSectionProps> = ({ files, links, allFiles, onFileClick }) => {
    const classes = useStyles();
    const count = files.length + links.length;
    const [expanded, setExpanded] = useState(false);

    if (count === 0) return null;

    const allItems: React.ReactNode[] = [
        ...files.map((file, i) => (
            <Stack direction="column" spacing={1} alignItems="flex-start" 
                key={`file-${i}`} onClick={() => onFileClick(file)}>
                <FileAttachment resName={file.name} file={file} />
            </Stack>
        )),
        ...links.map((link, i) => (
            <Stack direction="column" spacing={1} alignItems="flex-start" key={`link-${i}`}>
                <FileAttachment
                    resName={link}
                    file={allFiles?.length === 1 && links?.length === 1 ? allFiles[0] : undefined}
                />
            </Stack>
        )),
    ];

    const visibleItems = expanded ? allItems : allItems.slice(0, 1);
    const shouldScroll = expanded && count >= SCROLL_THRESHOLD;

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.label}>
                    {`Documents (${count})`}
                </Typography>
                {count > 1 && (
                    <IconButton
                        size="small"
                        className={classes.toggleBtn}
                        onClick={() => setExpanded(prev => !prev)}
                        disableRipple
                    >
                        {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </IconButton>
                )}
            </Box>
            <Stack
                direction="column"
                gap={1}
                className={shouldScroll ? classes.listScrollable : undefined}
            >
                {visibleItems}
            </Stack>
        </Box>
    );
};

export default EmailDocumentsSection;
